import { createAsyncTypes } from 'store/utils';
import { ConfigInputField } from 'services/configuration';
import { Customer } from 'services/customer';

// State Key
export const KEY = 'ticketing';

// Action Types
export const types = {
  SET_GENRES_CONFIG: `${KEY}/SET_GENRES_CONFIG`,
  SET_EVENTS_CONFIG: `${KEY}/SET_EVENTS_CONFIG`,
  SET_TIERS_CONFIG: `${KEY}/SET_TIERS_CONFIG`,
  SET_GROUP_TIERS_CONFIG: `${KEY}/SET_GROUP_TIERS_CONFIG`,
  SET_MEMBERSHIP_PRODUCT_CONFIG: `${KEY}/SET_MEMBERSHIP_PRODUCT_CONFIG`,
  RESET_TICKETING: `${KEY}/RESET_TICKETING`,
  GET_EVENTS_ASYNC: createAsyncTypes(`${KEY}/GET_EVENTS_ASYNC`),
  RESET_EVENTS: `${KEY}/RESET_EVENTS`,
  RESET_UPSELL_PRODUCTS: `${KEY}/RESET_UPSELL_PRODUCTS`,
  GET_EVENTDETAILS_ASYNC: createAsyncTypes(`${KEY}/GET_EVENTDETAILS_ASYNC`),
  GET_MEMBERSHIP_PRODUCT_ASYNC: createAsyncTypes(`${KEY}/GET_MEMBERSHIP_PRODUCT_ASYNC`),
  RESET_MEMBERSHIP_PRODUCT: `${KEY}/RESET_MEMBERSHIP_PRODUCT`,
  RESET_EVENT_DATA_ASYNC: createAsyncTypes(`${KEY}/RESET_EVENT_DATA`),
  CREATE_SESSION_ASYNC: createAsyncTypes(`${KEY}/CREATE_SESSION_ASYNC`),
  APPLY_PRICE_TYPE_PROMO_CODE_ASYNC: createAsyncTypes(`${KEY}/APPLY_PRICE_TYPE_PROMO_CODE_ASYNC`),
  RESET_PRICE_TYPE_PROMO_CODE: `${KEY}/RESET_PRICE_TYPE_PROMO_CODE`,
  RESET_APPLIED_PRICE_TYPE_PROMO: `${KEY}/RESET_APPLIED_PRICE_TYPE_PROMO`,
  RESET_EVENT_DETAILS: `${KEY}/RESET_EVENT_DETAILS`,
  RESET_UPSELL: `${KEY}/RESET_UPSELL`,
  RESET_DELIVERY: `${KEY}/RESET_DELIVERY`,
  VALIDATE_MEMBERSHIP_ASYNC: createAsyncTypes(`${KEY}/VALIDATE_MEMBERSHIP_ASYNC`),
  RESET_MEMBERSHIP_DETAILS: `${KEY}/RESET_MEMBERSHIP_DETAILS`,
  RESET_SESSION: `${KEY}/RESET_SESSION`,
  SAVE_PREFERRED_DELIVERY_METHOD: `${KEY}/SAVE_PREFERRED_DELIVERY_METHOD`,
  SAVE_DELIVERY_EMAIL: `${KEY}/SAVE_OTHER_EMAIL`,
  SAVE_DELIVERY_PHONE_NUMBER: `${KEY}/SAVE_OTHER_PHONE_NUMBER`,
  EMAIL_TICKETS_ASYNC: createAsyncTypes(`${KEY}/EMAIL_TICKETS_ASYNC`),
  TEXT_TICKETS_ASYNC: createAsyncTypes(`${KEY}/TEXT_TICKETS_ASYNC`),
  UPDATE_PRINT_COUNT_ASYNC: createAsyncTypes(`${KEY}/UPDATE_PRINT_COUNT_ASYNC`),
  SET_CONFIG_PATH: `${KEY}/SET_CONFIG_PATH`,
  SET_NAVIGATION_PATH: `${KEY}/SET_NAVIGATION_PATH`,
  SET_TIMER_OVERLAY: `${KEY}/SET_TIMER_OVERLAY`,
  SET_DELIVERY_STATUS: `${KEY}/SET_DELIVERY_STATUS`,
  SET_UPSELL: `${KEY}/SET_UPSELL`,
  SET_EVENT_DETAILS: `${KEY}/SET_EVENT_DETAILS`,
  SET_SELECTED_PROMO_CODE: `${KEY}/SET_SELECTED_PROMO_CODE`,
  RESET_SELECTED_PROMO_CODE: `${KEY}/RESET_SELECTED_PROMO_CODE`,
  SET_MEMBERSHIP_PRODUCT: `${KEY}/SET_MEMBERSHIP_PRODUCT`,
  RESET_ERROR: `${KEY}/RESET_ERROR`
};

export interface Seat {
  priceLevel: number;
  priceType: string;
  section: string;
  row: string;
  seat: string;
}

export interface Fee {
  name: string;
  price: number;
}

export interface Offer {
  basePrice: number;
  fees: Fee[];
  tier: string;
  type: string;
  typeId: string;
  typeLimit?: number;
  typePriority: number;
  availability: number;
  isPromoCodeOffer: boolean;
  isCityPassEligible?: boolean;
  cityPassClassId?: number;
  purchaseParams: any;
  groupId?: string;
  groupLimit?: number;
  groupPriority: number;
  tierPriority: number;
}

export interface Tier {
  tierAvailability: number;
  offers: Offer[];
  minPrice: number;
  maxPrice: number;
  hasPromoOffer: boolean;
}

export interface GroupOffer extends Offer {
  packageGroupId: string;
  eventId: string;
}

export interface GroupEvent {
  id: string;
  name: string;
  venue?: string;
  description: string;
  date: Date | string;
  availability: number;
  tiers: any;
  groupId: string;
  imageUrl?: string;
}

export interface Group {
  groupId: string;
  name: string;
  maxEvents: number;
  minEvents: number;
  events: GroupEvent[];
}

export interface GroupEvents {
  id: string;
  name: string;
  description: string;
  venue?: string;
  date: Date | string;
  detailsParams: any;
  availability: number;
  offers: Offer[];
  imageUrl?: string;
}

export interface Groups {
  id: string;
  name: string;
  maxEvents: number;
  minEvents: number;
  events: GroupEvents[];
}

export interface Event {
  id: string;
  name: string;
  alias?: string;
  date: Date | string;
  firstDate?: Date | string;
  lastDate?: Date | string;
  type?: string;
  venue?: string;
  detailsParams: any;
  upsell?: EventUpsell;
  genre?: string;
  genreAlias?: string;
  description?: string;
  imageUrl?: string;
}

interface EventUpsell {
  initialProductRequired: boolean;
  params: any;
}

export interface UpsellStore {
  initialProduct: {
    details?: EventDetails;
    eventsConfig?: Config;
    tiersConfig?: TierConfig;
    itemIdsInCartBeforeSelection?: string[];
    itemQtyInCartBeforeSelection?: number;
  };
  promptProduct: {
    details?: EventDetails;
    membership?: MembershipProduct;
    eventsConfig?: Config;
    tiersConfig?: TierConfig;
    itemIdsInCartBeforeSelection?: string[];
  };
  singleUpsellProductOffered: boolean;
}

export interface EventDetails extends Event {
  offers: Offer[];
  groups: Groups[];
}

export interface MembershipField extends ConfigInputField {
  requiredType?: boolean;
}

export interface MembershipType {
  name: string;
  price: number;
  required: boolean;
  fields: MembershipField[];
  reserveParams: any;
}

export interface MembershipLevel {
  name: string;
  basePrice: number;
  types: MembershipType[];
}

export interface MembershipProduct {
  id: string;
  name: string;
  levels: MembershipLevel[];
}

export interface MembershipProductConfig {
  selected: string;
}

export interface AppliedPromo extends EventDetails {
  promoCode?: string;
}

export interface Config {
  page: number;
  selected: string;
  isAutoLaunch?: boolean;
}
export interface TierSelection {
  tier: string;
  groupId?: string;
  date?: Date | string;
}
export interface TierConfig {
  page: number;
  selected: Record<string, TierSelection>;
}

export interface MembershipDetails {
  isLoggedIn: boolean;
  membershipParams?: any;
}

export interface PriceTypePromoCode {
  readonly loading: boolean;
  readonly error: undefined | string;
  readonly processed: boolean;
  readonly selectedCode?: undefined | string;
}

export type DeliveryMethod = 'email' | 'print' | 'textMessage';

export interface TicketingState {
  readonly events: Event[];
  readonly eventDetails: EventDetails | null;
  readonly membershipProduct: MembershipProduct | null;
  readonly upsell: UpsellStore;
  readonly upsellProducts: Event[];
  readonly session: string | null;
  readonly configPath: string;
  readonly navigationPath: string | null;
  readonly timerOverlay: boolean;
  readonly genresConfig: Config;
  readonly eventsConfig: Config;
  readonly tiersConfig: TierConfig;
  readonly membershipProductConfig: MembershipProductConfig;
  readonly loading: boolean;
  readonly error: undefined | string;
  readonly priceTypePromoCode: PriceTypePromoCode;
  readonly membershipDetails: MembershipDetails;
  readonly appliedPriceTypePromo?: string;
  readonly delivery: {
    readonly preferredMethod: DeliveryMethod | null;
    readonly email: string;
    readonly phone: string;
    readonly status: string;
  };
  readonly fetchCompleted: {
    events: boolean;
    eventDetails: boolean;
    membershipProduct: boolean;
    upsellProducts?: boolean;
  };
}

export interface ValidateMembershipResponse {
  isLoggedIn: boolean;
  session: string;
  customer: Customer;
  membershipParams?: any;
}

// CREATE SESSION ASYNC
export interface CreateSessionAsyncRequest {
  type: typeof types.CREATE_SESSION_ASYNC.REQUEST;
}

export interface CreateSessionAsyncError {
  type: typeof types.CREATE_SESSION_ASYNC.ERROR;
  payload: string;
}

export interface CreateSessionAsyncPending {
  type: typeof types.CREATE_SESSION_ASYNC.PENDING;
  payload: boolean;
}

export interface CreateSessionAsyncSuccess {
  type: typeof types.CREATE_SESSION_ASYNC.SUCCESS;
  payload: string;
}

// GET EVENTS ASYNC
export interface GetEventsAsyncRequest {
  type: typeof types.GET_EVENTS_ASYNC.REQUEST;
}

export interface GetEventsAsyncError {
  type: typeof types.GET_EVENTS_ASYNC.ERROR;
  payload: string;
}

export interface GetEventsAsyncPending {
  type: typeof types.GET_EVENTS_ASYNC.PENDING;
  payload: boolean;
}

export interface GetEventsAsyncSuccess {
  type: typeof types.GET_EVENTS_ASYNC.SUCCESS;
  payload: Event[];
}

// GET EVENT DETAILS ASYNC
export interface GetEventDetailsAsyncRequest {
  type: typeof types.GET_EVENTDETAILS_ASYNC.REQUEST;
}

export interface GetEventDetailsAsyncError {
  type: typeof types.GET_EVENTDETAILS_ASYNC.ERROR;
  payload: string;
}

export interface GetEventDetailsAsyncPending {
  type: typeof types.GET_EVENTDETAILS_ASYNC.PENDING;
  payload: boolean;
}

export interface GetEventDetailsAsyncSuccess {
  type: typeof types.GET_EVENTDETAILS_ASYNC.SUCCESS;
  payload: EventDetails;
}

// GET MEMBERSHIP PRODUCT ASYNC
export interface GetMembershipProductAsyncRequest {
  type: typeof types.GET_MEMBERSHIP_PRODUCT_ASYNC.REQUEST;
}

export interface GetMembershipProductAsyncError {
  type: typeof types.GET_MEMBERSHIP_PRODUCT_ASYNC.ERROR;
  payload: string;
}

export interface GetMembershipProductAsyncPending {
  type: typeof types.GET_MEMBERSHIP_PRODUCT_ASYNC.PENDING;
  payload: boolean;
}

export interface GetMembershipProductAsyncSuccess {
  type: typeof types.GET_MEMBERSHIP_PRODUCT_ASYNC.SUCCESS;
  payload: MembershipProduct;
}

// RESET EVENT DATA ASYNC
export interface ResetEventDataAsyncRequest {
  type: typeof types.RESET_EVENT_DATA_ASYNC.REQUEST;
}

export interface ResetEventDataAsyncError {
  type: typeof types.RESET_EVENT_DATA_ASYNC.ERROR;
  payload: string;
}

export interface ResetEventDataAsyncPending {
  type: typeof types.RESET_EVENT_DATA_ASYNC.PENDING;
  payload: boolean;
}

export interface ResetEventDataAsyncSuccess {
  type: typeof types.RESET_EVENT_DATA_ASYNC.SUCCESS;
  payload: EventDetails;
}
export interface SavePreferredDeliveryMethod {
  type: typeof types.SAVE_PREFERRED_DELIVERY_METHOD;
  payload: DeliveryMethod;
}

export interface SaveDeliveryEmail {
  type: typeof types.SAVE_DELIVERY_EMAIL;
  payload: string;
}

export interface SaveDeliveryPhoneNumber {
  type: typeof types.SAVE_DELIVERY_PHONE_NUMBER;
  payload: string;
}

export interface SetUpsell {
  type: typeof types.SET_UPSELL;
  payload: UpsellStore;
}

export interface SetDeliveryStatus {
  type: typeof types.SET_DELIVERY_STATUS;
  payload: string;
}

// Apply Price Type Promo Code
export interface ApplyPriceTypePromoCodeAsyncRequest {
  type: typeof types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.REQUEST;
  payload: string;
}

export interface ApplyPriceTypePromoCodeAsyncError {
  type: typeof types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.ERROR;
  payload: string;
}

export interface ApplyPriceTypePromoCodeAsyncPending {
  type: typeof types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.PENDING;
  payload: boolean;
}

export interface ApplyPriceTypePromoCodeAsyncSuccess {
  type: typeof types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.SUCCESS;
  payload: AppliedPromo;
}

export interface ValidateMembershipAsyncRequest {
  type: typeof types.VALIDATE_MEMBERSHIP_ASYNC.REQUEST;
  payload: {
    email?: string;
    firstName?: string;
    lastName?: string;
    memberId?: string;
    phone?: string;
    postalCode?: string;
    username?: string;
  };
}

export interface ValidateMembershipAsyncError {
  type: typeof types.VALIDATE_MEMBERSHIP_ASYNC.ERROR;
  payload: string;
}

export interface ValidateMembershipAsyncPending {
  type: typeof types.VALIDATE_MEMBERSHIP_ASYNC.PENDING;
  payload: boolean;
}

export interface ValidateMembershipAsyncSuccess {
  type: typeof types.VALIDATE_MEMBERSHIP_ASYNC.SUCCESS;
  payload: Partial<MembershipDetails>;
}

export interface EmailTicketsAsyncRequest {
  type: typeof types.EMAIL_TICKETS_ASYNC.REQUEST;
  payload: {
    orderId: string;
    transactionId: string;
    deliveryParams?: any;
  };
}

export interface EmailTicketsAsyncError {
  type: typeof types.EMAIL_TICKETS_ASYNC.ERROR;
  payload: string;
}

export interface EmailTicketsAsyncPending {
  type: typeof types.EMAIL_TICKETS_ASYNC.PENDING;
  payload: boolean;
}

export interface EmailTicketsAsyncSuccess {
  type: typeof types.EMAIL_TICKETS_ASYNC.SUCCESS;
  payload: string;
}

export interface TextTicketsAsyncRequest {
  type: typeof types.TEXT_TICKETS_ASYNC.REQUEST;
  payload: {
    orderId: string;
    transactionId: string;
    deliveryParams?: any;
  };
}

export interface TextTicketsAsyncPending {
  type: typeof types.TEXT_TICKETS_ASYNC.PENDING;
  payload: boolean;
}

export interface TextTicketsAsyncSuccess {
  type: typeof types.TEXT_TICKETS_ASYNC.SUCCESS;
  payload: string;
}

export interface TextTicketsAsyncError {
  type: typeof types.TEXT_TICKETS_ASYNC.ERROR;
  payload: string;
}

export interface UpdatePrintCountRequest {
  type: typeof types.UPDATE_PRINT_COUNT_ASYNC.REQUEST;
  payload: {
    ticketNumber: string;
    printCount: number;
    totalCount: number;
  };
}

// SET GENRES CONFIG
export interface SetGenresConfig {
  type: typeof types.SET_GENRES_CONFIG;
  payload: Config;
}

// SET EVENTS CONFIG
export interface SetEventsConfig {
  type: typeof types.SET_EVENTS_CONFIG;
  payload: Config;
}

// SET TIERS CONFIG
export interface SetTiersConfig {
  type: typeof types.SET_TIERS_CONFIG;
  payload: TierConfig;
}

// SET MEMBERSHIP PRODUCT CONFIG
export interface SetMembershipProductConfig {
  type: typeof types.SET_MEMBERSHIP_PRODUCT_CONFIG;
  payload: MembershipProductConfig;
}

// RESET TICKETING STATE
export interface ResetTicketing {
  type: typeof types.RESET_TICKETING;
}

export interface ResetPriceTypePromoCode {
  type: typeof types.RESET_PRICE_TYPE_PROMO_CODE;
}

export interface ResetAppliedPriceTypePromo {
  type: typeof types.RESET_APPLIED_PRICE_TYPE_PROMO;
}

export interface ResetEvents {
  type: typeof types.RESET_EVENTS;
}

export interface ResetUpsellProducts {
  type: typeof types.RESET_UPSELL_PRODUCTS;
}

export interface ResetEventDetails {
  type: typeof types.RESET_EVENT_DETAILS;
}

export interface ResetUpsell {
  type: typeof types.RESET_UPSELL;
}

export interface ResetMembershipProduct {
  type: typeof types.RESET_MEMBERSHIP_PRODUCT;
}

export interface ResetSession {
  type: typeof types.RESET_SESSION;
}

export interface ResetSelectedPromoCode {
  type: typeof types.RESET_SELECTED_PROMO_CODE;
}

export interface SetEventDetails {
  type: typeof types.SET_EVENT_DETAILS;
  payload: EventDetails;
}

export interface SetMembershipProduct {
  type: typeof types.SET_MEMBERSHIP_PRODUCT;
  payload: MembershipProduct;
}

export interface SetConfigPath {
  type: typeof types.SET_CONFIG_PATH;
  payload: string;
}

export interface SetNavigationPath {
  type: typeof types.SET_NAVIGATION_PATH;
  payload: string | null;
}

export interface SetTimerOverlay {
  type: typeof types.SET_TIMER_OVERLAY;
  payload: boolean;
}

export interface SetSelectedPromoCode {
  type: typeof types.SET_SELECTED_PROMO_CODE;
  payload: string;
}

export type TicketingActionTypes =
  | CreateSessionAsyncRequest
  | CreateSessionAsyncError
  | CreateSessionAsyncPending
  | CreateSessionAsyncSuccess
  | GetEventsAsyncRequest
  | GetEventsAsyncError
  | GetEventsAsyncPending
  | GetEventsAsyncSuccess
  | GetEventDetailsAsyncRequest
  | GetEventDetailsAsyncError
  | GetEventDetailsAsyncPending
  | GetEventDetailsAsyncSuccess
  | GetMembershipProductAsyncSuccess
  | ResetEventDataAsyncRequest
  | ResetEventDataAsyncError
  | ResetEventDataAsyncPending
  | ResetEventDataAsyncSuccess
  | ApplyPriceTypePromoCodeAsyncRequest
  | ApplyPriceTypePromoCodeAsyncError
  | ApplyPriceTypePromoCodeAsyncPending
  | ApplyPriceTypePromoCodeAsyncSuccess
  | ValidateMembershipAsyncRequest
  | EmailTicketsAsyncRequest
  | TextTicketsAsyncRequest
  | UpdatePrintCountRequest
  | SetGenresConfig
  | SetEventsConfig
  | SetTiersConfig
  | SetMembershipProductConfig
  | ResetTicketing
  | ResetPriceTypePromoCode
  | ResetAppliedPriceTypePromo
  | ResetEvents
  | ResetEventDetails
  | ResetUpsell
  | ResetMembershipProduct
  | ResetSession
  | ResetSelectedPromoCode
  | SavePreferredDeliveryMethod
  | SaveDeliveryEmail
  | SaveDeliveryPhoneNumber
  | SetUpsell
  | SetEventDetails
  | SetNavigationPath
  | SetSelectedPromoCode;
